import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi/useApi";
import { useNotification } from "~hooks/useNotification";
import { PermissionProducts } from "~pages/Users/enums/permissionProducts";
import type { User, UserResponse } from "~types/user";
import type { AddUserForm } from "../types";

const useAddPermissionUser = (onSuccess: (userData: User) => void) => {
  const { sport, organizationId } = useParams();
  const request = useApi();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<UserResponse, Error, AddUserForm>({
    mutationFn: async (userData: AddUserForm) => {
      const { selectedProduct, ...rest } = userData;
      return await request(`${sport}/o/${organizationId}/users`, {
        method: "POST",
        body: {
          ...rest,
          isForTeamManagerProduct: selectedProduct === PermissionProducts.TeamManager,
        },
      });
    },
    onSuccess: async (response: { data: User[] }) => {
      if (response?.data?.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: ["permission-users", sport, organizationId],
        });

        notification.success({
          message: formatMessage({
            id: "permissions.user.actions.add.success",
          }),
          placement: "bottomRight",
        });

        onSuccess(response.data[0]);
      }
    },
  });
};

export default useAddPermissionUser;
